/*
 * CAPTURE-OWNED MODULE
 * Template List Page for capture-ui
 * Displays templates as a responsive card grid with thumbnails,
 * multi-select checkboxes, context menu, and bulk actions.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { captureAPI } from '../services/api';
import { saveTemplate } from '../api/captureTemplates';
import { formatDocType } from '../utils/formatDocType';
import { terminalOcrMessage } from '../utils/ocrReadiness';
import { duplicateChoiceFromUploadError } from '../utils/ingestionGuards';
import './TemplateListPage.css';

const TemplateListPage = ({ onOpenTemplate }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openError, setOpenError] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [creating, setCreating] = useState(null);
  const [createError, setCreateError] = useState(null);
  // Gate C block-and-choose: { file, existingDocument } when the picked sample
  // matches a document the tenant already has.
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);
  const fileInputRef = useRef(null);

  // Selection state
  const [selectedTemplates, setSelectedTemplates] = useState([]);

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);

  // Inline rename state
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const renameInputRef = useRef(null);

  // Thumbnail signed URLs keyed by template id
  const [thumbnailUrls, setThumbnailUrls] = useState({});

  // Load templates on component mount
  useEffect(() => {
    loadTemplates();
  }, []);

  // Close context menu on outside click / scroll
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [contextMenu]);

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[TemplateListPage] Loading templates...');
      const response = await captureAPI.templates.list({
        search: searchTerm || undefined,
        limit: 50
      });
      if ((response.ok || response.success) && response.templates) {
        setTemplates(response.templates);
        console.log('[TemplateListPage] Loaded templates:', response.templates.length);
        // Fetch thumbnail URLs in background
        fetchThumbnailUrls(response.templates);
      } else {
        throw new Error(response.error || 'Failed to load templates');
      }
    } catch (err) {
      console.error('[TemplateListPage] Error loading templates:', err);
      setError(err.message);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch signed file URLs for template thumbnails (non-blocking)
  const fetchThumbnailUrls = async (templateList) => {
    const urlMap = {};
    await Promise.allSettled(
      templateList
        .filter(t => t.source_document_id)
        .map(async (t) => {
          try {
            const data = await captureAPI.getDocumentFileUrl(t.source_document_id);
            if (data?.url) {
              urlMap[t.id] = data.url;
            }
          } catch (err) {
            console.warn('[TemplateListPage] Thumbnail fetch failed for', t.id, err?.message);
          }
        })
    );
    setThumbnailUrls(prev => ({ ...prev, ...urlMap }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadTemplates();
  };

  // ── Create Template: file pick → upload → AI build → navigate ──
  const triggerFilePicker = () => {
    if (creating) return;
    setCreateError(null);
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  const waitForOcrReady = async (docId, { timeoutMs = 90000, intervalMs = 2000 } = {}) => {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      let terminal = null;
      try {
        const stats = await captureAPI.getOcrWordStats({ documentId: docId });
        if (stats?.totalWords > 0) {
          console.log(`[TemplateListPage] OCR ready: ${stats.totalWords} words (${Math.round((Date.now() - start) / 1000)}s)`);
          return stats;
        }
        // Terminal non-OCR state (e.g. duplicate_pending_review) — OCR will
        // never produce words; stop polling and report the real reason.
        terminal = terminalOcrMessage(stats?.status);
      } catch (err) {
        console.warn('[TemplateListPage] OCR stats poll error (will retry):', err?.message);
      }
      if (terminal) throw new Error(terminal);
      await new Promise(r => setTimeout(r, intervalMs));
    }
    throw new Error('OCR is still processing. Please wait a moment and try again.');
  };

  // Upload a sample and proceed into the builder. Factored out so the
  // "Upload as new" override (Gate C) can re-run the exact same flow.
  const proceedWithUpload = async (file, { overrideDuplicate = false } = {}) => {
    setCreateError(null);
    setDuplicatePrompt(null);
    try {
      setCreating('uploading');
      console.log('[TemplateListPage] Uploading file:', file.name, overrideDuplicate ? '(override)' : '');
      // purpose='template_source' prevents this document from appearing in the
      // Processing tab and suppresses auto-extraction after OCR.
      const uploadRes = await captureAPI.uploadFile(file, { purpose: 'template_source', overrideDuplicate });
      const doc = uploadRes?.captureDocument || uploadRes?.document || uploadRes?.capture_document;
      const docId = doc?.id;
      if (!docId) {
        console.error('[TemplateListPage] Upload response keys:', Object.keys(uploadRes || {}), uploadRes);
        throw new Error('Upload succeeded but no document ID returned');
      }
      console.log('[TemplateListPage] Uploaded document:', docId);
      setCreating('ocr');
      await waitForOcrReady(docId);
      navigate('/template-builder', {
        state: { documentId: docId, filename: file.name, fromWorkflow: 'template-studio' },
      });
    } catch (err) {
      // Gate C block-and-choose: a live duplicate → offer Use existing / Upload as new.
      const existing = duplicateChoiceFromUploadError(err);
      if (existing) {
        console.log('[TemplateListPage] Duplicate detected — prompting for decision', existing);
        setDuplicatePrompt({ file, existingDocument: existing });
        return;
      }
      console.error('[TemplateListPage] Create template failed:', err);
      const msg = err?.response?.data?.error || err?.serverDetails?.error || err?.message || 'Failed to create template';
      setCreateError(msg);
    } finally {
      setCreating(null);
    }
  };

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    proceedWithUpload(file);
  };

  // "Use existing": build the template from the document we already have.
  // TemplateBuilderPage operates on a documentId from navigation state, so an
  // existing doc's id slots straight in (it already has OCR words).
  const handleUseExistingForTemplate = () => {
    const existing = duplicatePrompt?.existingDocument;
    setDuplicatePrompt(null);
    if (!existing?.id) return;
    navigate('/template-builder', {
      state: { documentId: existing.id, filename: existing.filename, fromWorkflow: 'template-studio' },
    });
  };

  // "Upload as new": re-run the upload with the override so the server bypasses
  // the duplicate block and processes the file as a fresh document.
  const handleUploadAsNewTemplate = () => {
    const file = duplicatePrompt?.file;
    if (file) proceedWithUpload(file, { overrideDuplicate: true });
  };

  // ── Open template ──
  const handleOpenTemplateClick = useCallback((template) => {
    setOpenError(null);
    console.log('[TemplateListPage] Opening template:', { templateId: template.id, name: template.name });
    if (onOpenTemplate) {
      onOpenTemplate(template.id);
    } else {
      const docId = template.source_document_id;
      if (!docId) {
        setOpenError(`Template "${template.name}" has no source document.`);
        return;
      }
      navigate(`/documents/${docId}`);
    }
  }, [onOpenTemplate, navigate]);

  // ── Delete single template ──
  const handleDeleteTemplate = async (template) => {
    setOpenError(null);
    setDeleteMessage(null);
    const confirmed = window.confirm(
      `Delete "${template.name}"? This will permanently remove the template, its zones/fields/transforms, ` +
      'the source document, and any related data. This cannot be undone.'
    );
    if (!confirmed) return;

    setDeleting(template.id);
    try {
      const response = await captureAPI.templates.delete(template.id);
      if (response.ok) {
        setTemplates(prev => prev.filter(t => t.id !== template.id));
        setSelectedTemplates(prev => prev.filter(id => id !== template.id));
        setDeleteMessage({ type: 'success', text: `"${template.name}" deleted.` });
      } else {
        throw new Error(response.error || 'Delete failed');
      }
    } catch (err) {
      console.error('[TemplateListPage] Delete failed:', err);
      setDeleteMessage({
        type: 'error',
        text: err.response?.status === 404
          ? 'Template not found (may already be deleted).'
          : `Failed to delete: ${err.message}`,
      });
    } finally {
      setDeleting(null);
    }
  };

  // ── Duplicate template (placeholder — opens editor for now) ──
  const handleDuplicateTemplate = useCallback((templateId) => {
    console.log('[TemplateListPage] Duplicate requested for:', templateId);
    // TODO: implement server-side duplicate; for now just open the template
    if (onOpenTemplate) onOpenTemplate(templateId);
  }, [onOpenTemplate]);

  // ── Selection helpers ──
  const handleToggleSelection = useCallback((templateId) => {
    setSelectedTemplates(prev =>
      prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedTemplates.length === templates.length) {
      setSelectedTemplates([]);
    } else {
      setSelectedTemplates(templates.map(t => t.id));
    }
  }, [selectedTemplates.length, templates]);

  const handleBulkDelete = async () => {
    if (selectedTemplates.length === 0) return;
    const confirmed = window.confirm(`Delete ${selectedTemplates.length} template(s)? This cannot be undone.`);
    if (!confirmed) return;

    setDeleting('bulk');
    try {
      await Promise.all(
        selectedTemplates.map(id => captureAPI.templates.delete(id))
      );
      setTemplates(prev => prev.filter(t => !selectedTemplates.includes(t.id)));
      setDeleteMessage({ type: 'success', text: `${selectedTemplates.length} template(s) deleted.` });
      setSelectedTemplates([]);
    } catch (err) {
      console.error('[TemplateListPage] Bulk delete failed:', err);
      setDeleteMessage({ type: 'error', text: `Bulk delete failed: ${err.message}` });
      loadTemplates();
    } finally {
      setDeleting(null);
    }
  };

  // ── Context menu ──
  const handleContextMenu = useCallback((e, template) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, template });
  }, []);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  // ── Inline rename ──
  const startRename = useCallback((template) => {
    setRenamingId(template.id);
    setRenameValue(template.name || '');
  }, []);

  const commitRename = useCallback(async () => {
    const trimmed = renameValue.trim();
    if (!renamingId || !trimmed) {
      setRenamingId(null);
      return;
    }
    // Skip API call if name unchanged
    const current = templates.find(t => t.id === renamingId);
    if (current && current.name === trimmed) {
      setRenamingId(null);
      return;
    }
    setRenameSaving(true);
    try {
      await saveTemplate(renamingId, { name: trimmed });
      setTemplates(prev => prev.map(t =>
        t.id === renamingId ? { ...t, name: trimmed } : t
      ));
      console.log(`[TemplateListPage] Renamed ${renamingId} to "${trimmed}"`);
    } catch (err) {
      console.error('[TemplateListPage] Rename failed:', err);
      setDeleteMessage({ type: 'error', text: `Rename failed: ${err?.response?.data?.error || err?.message}` });
    } finally {
      setRenameSaving(false);
      setRenamingId(null);
    }
  }, [renamingId, renameValue, templates]);

  const cancelRename = useCallback(() => {
    setRenamingId(null);
    setRenameValue('');
  }, []);

  // Auto-focus rename input when it appears
  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  // ── Thumbnail URL helper ──
  const getThumbnailUrl = (template) => {
    if (thumbnailUrls[template.id]) return thumbnailUrls[template.id];
    if (template.thumbnail_url) return template.thumbnail_url;
    return null;
  };

  // ── Inline alert component ──
  const InlineAlert = ({ message, type, onDismiss }) => (
    <div className={`inline-alert inline-alert-${type}`}>
      <span>{message}</span>
      <button className="inline-alert-dismiss" onClick={onDismiss}>✕</button>
    </div>
  );

  // ── Loading state ──
  if (loading) {
    return (
      <div className="template-list-page">
        <div className="page-header"><h1>Template Studio</h1></div>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading templates...</p>
        </div>
      </div>
    );
  }

  // ── Error state ──
  if (error) {
    return (
      <div className="template-list-page">
        <div className="page-header"><h1>Template Studio</h1></div>
        <div className="error-container">
          <h3>Error Loading Templates</h3>
          <p className="error-message">{error}</p>
          <button onClick={loadTemplates} className="btn btn-primary">Retry</button>
        </div>
      </div>
    );
  }

  // ── Main render ──
  return (
    <div className="template-list-page">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelected}
        accept="application/pdf,image/*"
        style={{ display: 'none' }}
      />

      {/* Header — title only */}
      <div className="page-header">
        <h1>Template Studio</h1>
      </div>

      {/* Actions bar — Create button + search */}
      <div className="actions-bar">
        <button
          className="btn btn-primary"
          onClick={triggerFilePicker}
          disabled={!!creating}
        >
          {creating === 'uploading' ? 'Uploading…' : creating === 'ocr' ? 'Running OCR…' : '+ Create Template'}
        </button>
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="btn btn-secondary">Search</button>
        </form>
      </div>

      {/* Bulk actions bar */}
      {selectedTemplates.length > 0 && (
        <div className="bulk-actions-bar">
          <span className="selection-count">
            {selectedTemplates.length} selected
          </span>
          <button className="btn btn-secondary btn-sm" onClick={handleSelectAll}>
            {selectedTemplates.length === templates.length ? 'Deselect All' : 'Select All'}
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={handleBulkDelete}
            disabled={deleting === 'bulk'}
          >
            {deleting === 'bulk' ? 'Deleting…' : 'Delete Selected'}
          </button>
        </div>
      )}

      {/* Gate C — duplicate block-and-choose */}
      {duplicatePrompt && (
        <div className="dup-choice" role="dialog" aria-label="Duplicate document">
          <div className="dup-choice__body">
            <strong>This matches a document you already have</strong>
            <p className="dup-choice__meta">
              {duplicatePrompt.existingDocument.filename || 'Existing document'}
              {duplicatePrompt.existingDocument.created_at
                ? ` — uploaded ${new Date(duplicatePrompt.existingDocument.created_at).toLocaleDateString()}`
                : ''}
            </p>
            <div className="dup-choice__actions">
              <button type="button" className="btn btn-primary" onClick={handleUseExistingForTemplate} disabled={!!creating}>
                Use existing
              </button>
              <button type="button" className="btn" onClick={handleUploadAsNewTemplate} disabled={!!creating}>
                Upload as new
              </button>
              <button type="button" className="btn btn-link" onClick={() => setDuplicatePrompt(null)} disabled={!!creating}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inline alerts */}
      {createError && <InlineAlert message={createError} type="error" onDismiss={() => setCreateError(null)} />}
      {openError && <InlineAlert message={openError} type="error" onDismiss={() => setOpenError(null)} />}
      {deleteMessage && (
        <InlineAlert
          message={deleteMessage.text}
          type={deleteMessage.type}
          onDismiss={() => setDeleteMessage(null)}
        />
      )}

      {/* Templates */}
      <div className="templates-section">
        {templates.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
              </svg>
            </div>
            <h3>No Templates Found</h3>
            <p>
              {searchTerm
                ? `No templates match "${searchTerm}". Try a different search term.`
                : 'No templates have been created yet. Create your first template to get started.'}
            </p>
            <button
              className="btn btn-primary"
              onClick={triggerFilePicker}
              disabled={!!creating}
            >
              {creating ? 'Creating…' : '+ Create Template'}
            </button>
          </div>
        ) : (
          <div className="template-grid">
            {templates.map((template) => {
              const thumbUrl = getThumbnailUrl(template);
              const isSelected = selectedTemplates.includes(template.id);
              return (
                <div
                  key={template.id}
                  className={`template-card${isSelected ? ' template-card-selected' : ''}`}
                  onClick={() => handleOpenTemplateClick(template)}
                  onContextMenu={(e) => handleContextMenu(e, template)}
                >
                  {/* Selection checkbox */}
                  <div className="card-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleToggleSelection(template.id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>

                  {/* Status pill */}
                  <div className="card-status">
                    <span className={`template-status-pill ${template.status === 'published' || template.published_at ? 'published' : 'draft'}`}>
                      {template.status === 'published' || template.published_at ? 'Published' : 'Draft'}
                    </span>
                  </div>

                  {/* Thumbnail */}
                  <div className="card-thumbnail">
                    {thumbUrl ? (
                      <img
                        src={thumbUrl}
                        alt={template.name}
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div className="placeholder-thumbnail" style={thumbUrl ? { display: 'none' } : undefined}>
                      <svg className="placeholder-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="card-info">
                    {renamingId === template.id ? (
                      <input
                        ref={renameInputRef}
                        className="template-rename-input"
                        type="text"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                          if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                        }}
                        onBlur={commitRename}
                        onClick={(e) => e.stopPropagation()}
                        disabled={renameSaving}
                        maxLength={200}
                      />
                    ) : (
                      <h3 className="template-name">{template.name}</h3>
                    )}
                    <div className="template-meta">
                      <span className="field-count">
                        {template.field_count || 0} fields
                      </span>
                      {template.region_count > 0 && (
                        <span className="region-count">
                          {template.region_count} regions
                        </span>
                      )}
                      <span className="template-type">{formatDocType(template.doc_type)}</span>
                    </div>
                    <div className="template-stats">
                      <span className="created-date">
                        {template.created_at
                          ? new Date(template.created_at).toLocaleDateString()
                          : ''}
                      </span>
                      {/* Settings entry point. The card's own click opens the
                          EDITOR (handleOpenTemplateClick) and must keep doing
                          so — stopPropagation keeps this from bubbling into it.
                          Until this button existed the settings page was only
                          reachable via a context-menu item and a deep link from
                          Extraction Insights; nothing on the primary flow. */}
                      <button
                        className="card-settings-link"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/template-settings/${template.id}`);
                        }}
                        title="Template settings — page selection, identifiers, anchors"
                      >
                        ⚙ Settings
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer Stats */}
      {templates.length > 0 && (
        <div className="page-footer">
          <p>{templates.length} template{templates.length !== 1 ? 's' : ''} found</p>
        </div>
      )}

      {/* Context menu */}
      {contextMenu && (
        <>
          <div className="context-menu-overlay" onClick={closeContextMenu} />
          <div
            className="context-menu"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            <button onClick={() => { handleOpenTemplateClick(contextMenu.template); closeContextMenu(); }}>
              Edit Template
            </button>
            <button onClick={() => { startRename(contextMenu.template); closeContextMenu(); }}>
              Rename
            </button>
            <button onClick={() => { navigate(`/template-settings/${contextMenu.template.id}`); closeContextMenu(); }}>
              Settings
            </button>
            <button onClick={() => { handleDuplicateTemplate(contextMenu.template.id); closeContextMenu(); }}>
              Duplicate
            </button>
            <button
              className="danger"
              onClick={() => { handleDeleteTemplate(contextMenu.template); closeContextMenu(); }}
            >
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default TemplateListPage;
